import _ from 'lodash';
import * as reasonCodeUtilities from './components/extensionviews/reasonCodes.utilities';
import type { ReasonCode } from './components/extensionviews/reasonCodes.utilities';
import * as labels from './labels';
import { VantageNames } from '@/vantage/constants';
import type { StagedContext } from '@/vantage/types/Context.types';
import type { SimplifiedNode } from '@/vantage/types/SimplifiedNode.types';
import { getReasonCodeEventTime } from '@/vantage/context';
import { sqConditionsApi } from '@/sdk/api/ConditionsApi';
import { sqConditionMonitorsApi } from '@/sdk/api/ConditionMonitorsApi';
import { sqItemsApi } from '@/sdk/api/ItemsApi';
import { sqTableDefinitionsApi } from '@/sdk/api/TableDefinitionsApi';

interface ContextHookParams {
  node: SimplifiedNode;
  extension: any;
  context: StagedContext;
  tableIds: Record<string, string>;
}

interface ReasonCodesContextHookParams extends ContextHookParams {
  context: StagedContext & { content: { payload: { reasonCodes: ReasonCode[] }; reasonCodePath: string[] } };
}

export default {
  suppression: {
    creation: {
      pre: async ({ context, tableIds }: ContextHookParams) => {
        const {
          resetConditionType,
          parentCondition: parentConditionId,
          basicResetConditionFormula,
          basicResetCondition,
          suppressionExpiration,
        } = context.content.payload;
        let resetCondition = null;
        if (resetConditionType === 'basic') {
          const { data: parentCondition } = await sqConditionsApi.getCondition({ id: parentConditionId });

          resetCondition = await sqConditionsApi
            .createCondition({
              name: `Basic Reset Condition Targeting ${parentCondition.name}`,
              scopedTo: parentCondition.scopedTo,
              formula: basicResetConditionFormula.replace(basicResetCondition.label, '$param'),
              parameters: [`param=${basicResetCondition.value}`],
              datasourceClass: 'Vantage',
              datasourceId: 'VantageSuppression',
              description: `Suppression Reset Condition Targeting ${parentConditionId}`,
              additionalProperties: [{ name: 'Reset Target', value: parentConditionId }],
            })
            .then(({ data }) => data);
        }

        const { data: evidenceTable } = await sqTableDefinitionsApi.getTableDefinition({ id: tableIds.evidence });
        if (!evidenceTable.subscription?.id) {
          return;
        }
        const newSuppression = {
          conditionId: parentConditionId,
          suppressionStartTime: new Date().toISOString(),
          suppressionEndTime: new Date(suppressionExpiration).toISOString(),
          ...(resetCondition && { suppressionEndConditionId: resetCondition.id }),
        };

        await sqConditionMonitorsApi.suppressConditions([newSuppression], {
          id: evidenceTable.subscription.id,
        });
      },
    },
    resolution: {
      pre: async ({ context, tableIds }: ContextHookParams) => {
        const { parentCondition: parentConditionId } = context.content.payload;
        const { data: evidenceTable } = await sqTableDefinitionsApi.getTableDefinition({ id: tableIds.evidence });
        if (!evidenceTable.subscription?.id) {
          return;
        }

        const { data: suppressedConditions } = await sqConditionMonitorsApi.getConditionMonitorSuppressions({
          id: evidenceTable.subscription.id,
        });

        const resetConditionToArchive = suppressedConditions
          .find((s) => s.conditionId?.toUpperCase() === parentConditionId?.toUpperCase())
          ?.suppressionEndConditionId?.toUpperCase();
        if (resetConditionToArchive && resetConditionToArchive !== 'NULL') {
          sqItemsApi.archiveItem({ id: resetConditionToArchive });
        }

        await sqConditionMonitorsApi.unsuppressConditions([parentConditionId], {
          id: evidenceTable.subscription.id,
        });
      },
    },
  },
  reason_codes: {
    creation: {
      pre: async ({ node, context, extension, tableIds }: ReasonCodesContextHookParams) => {
        if (extension.labelingWindowHours) {
          const isInLabelingWindow = reasonCodeUtilities.labelingWindowValidator(extension);
          if (node.leafCapsules.some((row) => !isInLabelingWindow(parseInt(row[VantageNames.Columns.DatumID], 10)))) {
            throw new Error('A selected capsule is too old to be given a reason code.');
          }
        }

        const conditionIds: string[] = _.uniq(node.leafCapsules.map((row) => row[VantageNames.Columns.ConditionID]));
        const reasonCodeSets = await reasonCodeUtilities.reasonCodesForConditions(tableIds.reasonCodes, conditionIds);
        const labelPath = context.content.payload.reasonCodes.map((rc: ReasonCode) => rc.label);
        if (Object.values(reasonCodeSets).some((rcs) => !reasonCodeUtilities.findReasonCode(rcs, labelPath))) {
          throw new Error('The selected reason code is not valid for some of the selected capsules.');
        }

        const categoryNames = context.content.payload.reasonCodes
          .map((rc: ReasonCode) => rc.category)
          .filter((name: string | undefined) => name);
        const categories = await labels.findCategoryHierarchy(
          categoryNames.length ? categoryNames : extension.labelCategories,
        );
        await Promise.all(
          node.leafCapsules.map(async (capsule) => {
            const itemId = capsule[VantageNames.Columns.ConditionID];
            const datumId = capsule[VantageNames.Columns.DatumID];
            const [startTime, endTime] = getReasonCodeEventTime(context, datumId);

            await labels.add({
              itemId,
              datumId,
              categories,
              labelNames: labelPath,
              start: startTime,
              end: endTime,
              metadata: {
                identifier: VantageNames.Extensions.ReasonCodes,
                kind: VantageNames.Kinds.Capsule,
                kind_id: datumId,
                content: context.content,
                userMetadata: {},
              },
            });
          }),
        );
      },
    },
    deletion: {
      pre: async ({ node, extension }: ReasonCodesContextHookParams) => {
        if (extension.labelingWindowHours) {
          const isInLabelingWindow = reasonCodeUtilities.labelingWindowValidator(extension);
          if (
            node.leafCapsules.some(
              (capsule) => !isInLabelingWindow(parseInt(capsule[VantageNames.Columns.DatumID], 10)),
            )
          ) {
            throw new Error('A selected capsule is too old to have its reason code removed.');
          }
        }
      },
    },
  },
};
